import { Badge, Box, Text, useMantineTheme } from '@mantine/core';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';

import { CitationCard } from './citation-card';
import { mapCitationSourceToCardData } from './citation-card/CitationCard.utils';
import CitationPopover from './CitationPopover';
import { useMarkdownRendererContext } from './markdown-renderer/MarkdownRenderer.context';

import {
  isCitationSourceEmpty,
  isCitationSourceFetched,
} from '../utils/sources.utils';

import type { CitationSource, StreamingCitationSources } from '../types';
import type { ReactNode } from 'react';

type FootnoteCitationProps = {
  /** 1-based citation number matching the footnote index */
  number: number;
  /** Sparse array of citation sources indexed by (number - 1); may contain undefined slots for unresolved citations */
  sources: StreamingCitationSources;
  onRemove?: () => void;
  removeDisabled?: boolean;
  /**
   * The chip's state in a version diff: 'add' a new source, 'del' a dropped
   * one, 'swap' the same number now pointing at a different source.
   */
  diffType?: DiffCitationType;
  /** Renders inside a version diff, where unchanged chips go grey. */
  inDiff?: boolean;
  /**
   * For a 'swap' chip, the source this number pointed at in the old version.
   * The popover shows it under the new source, faded and marked Removed.
   */
  replacedSource?: CitationSource;
};

export type DiffCitationType = 'add' | 'del' | 'swap';

type BadgeVars = Record<'--badge-bg' | '--badge-color' | '--badge-bd', string>;

/**
 * Chip colours inside a diff. Kept chips go grey so changed ones stand out:
 * new and swapped sources read blue, dropped ones dashed and struck through.
 */
const DIFF_BADGE_VARS: Record<DiffCitationType | 'kept', BadgeVars> = {
  kept: {
    '--badge-bg': 'var(--klue-color-keyline)',
    '--badge-color': 'var(--klue-color-muted-strong)',
    '--badge-bd': 'var(--klue-border-hairline) solid var(--klue-color-line)',
  },
  add: {
    '--badge-bg': 'var(--klue-intel-surface)',
    '--badge-color': 'var(--klue-intel-ink)',
    '--badge-bd': 'var(--klue-border-hairline) solid var(--klue-intel-line)',
  },
  swap: {
    '--badge-bg': 'var(--klue-intel-surface)',
    '--badge-color': 'var(--klue-intel-ink)',
    '--badge-bd': 'var(--klue-border-hairline) solid var(--klue-intel-line)',
  },
  del: {
    '--badge-bg': 'transparent',
    '--badge-color': 'var(--klue-color-muted-soft)',
    '--badge-bd':
      'var(--klue-border-hairline) dashed var(--klue-color-muted-soft)',
  },
};

function DefaultCitationContent({
  source,
  onRemove,
  removeDisabled,
}: {
  source: CitationSource;
  onRemove?: () => void;
  removeDisabled?: boolean;
}) {
  const cardData = useMemo(() => {
    const { authorId: _, ...data } = mapCitationSourceToCardData(source);
    return data;
  }, [source]);

  return (
    <CitationCard
      {...cardData}
      onRemove={onRemove}
      removeDisabled={removeDisabled}
    />
  );
}

export default function FootnoteCitation({
  number,
  sources,
  onRemove,
  removeDisabled = false,
  diffType,
  inDiff = false,
  replacedSource,
}: FootnoteCitationProps) {
  const { renderCitationContentOverride } = useMarkdownRendererContext();
  const { primaryColor } = useMantineTheme();
  const source = sources[number - 1];

  const isFetched = source ? isCitationSourceFetched(source) : false;
  const isEmpty = source ? isCitationSourceEmpty(source) : true;

  const diffVars =
    diffType || inDiff ? DIFF_BADGE_VARS[diffType ?? 'kept'] : undefined;

  const getBadgeColor = (): string => {
    if (!source || !isFetched) return 'gray';
    if (isEmpty) return 'orange';
    return primaryColor;
  };
  const badgeColor = getBadgeColor();

  const badge = (
    <Badge
      variant="light"
      color={badgeColor}
      data-test-id={`footnote-citation-badge-${number}`}
      vars={diffVars ? () => ({ root: diffVars }) : undefined}
      leftSection={
        diffType === 'swap' ? (
          <ArrowsClockwiseIcon size={9} aria-label="Different source" />
        ) : undefined
      }
      style={{
        verticalAlign: 'text-bottom',
        cursor: source ? 'pointer' : 'default',
        textDecoration: diffType === 'del' ? 'line-through' : undefined,
      }}
    >
      {number}
    </Badge>
  );

  // No source at this index → orphan marker (e.g., the BE allocated `[^N]`
  // optimistically while the resolve ran async, and the resolve later
  // returned None / 404). Render nothing so the rendered text reads
  // continuously without an unclickable gray badge.
  if (!source) {
    return null;
  }

  if (isFetched && isEmpty) {
    return (
      <span
        style={{ marginLeft: 2 }}
        data-test-id={`footnote-citation-${number}`}
      >
        {badge}
      </span>
    );
  }

  const contentFor = (cited: CitationSource): ReactNode =>
    renderCitationContentOverride?.({
      source: cited,
      onRemove,
      removeDisabled,
    }) ?? (
      <DefaultCitationContent
        source={cited}
        onRemove={onRemove}
        removeDisabled={removeDisabled}
      />
    );

  const content =
    diffType === 'swap' && replacedSource ? (
      <>
        {contentFor(source)}
        <Box
          opacity={0.6}
          style={{
            borderTop:
              'var(--klue-border-hairline) solid var(--klue-color-line)',
          }}
          data-test-id={`footnote-citation-replaced-${number}`}
        >
          <Text size="xs" fw={600} c="dimmed" px="sm" pt="xs">
            Removed
          </Text>
          {contentFor(replacedSource)}
        </Box>
      </>
    ) : (
      contentFor(source)
    );

  return (
    <span
      style={{ marginLeft: 2 }}
      data-test-id={`footnote-citation-${number}`}
      data-citation-url={source.url}
      data-copy-exclude
    >
      <CitationPopover content={content} width={360}>
        {badge}
      </CitationPopover>
    </span>
  );
}
